'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { getPagePermission } from '@/config/permissions.config';
import { checkPagePermission } from '@/lib/permissions';
import { useLinkClickLog } from '@/hooks/use-link-click-log';
import { useLocale } from '@/hooks/use-locale';
import { useLoginRedirect } from '@/hooks/use-login-redirect';
import { useSession } from '@/hooks/use-session';
import Demo7Layout from '../components/layouts/demo7/layout';

export default function ProtectedLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session, status } = useSession();
  const router = useRouter();
  useLinkClickLog();
  const { internalPathname } = useLocale();
  const loginRedirect = useLoginRedirect();
  const [guestPermissions, setGuestPermissions] = useState<string[] | null>(
    null,
  );
  const [guestLoading, setGuestLoading] = useState(false);

  const isLoading = status === 'loading';
  const isAuthenticated = status === 'authenticated';

  // Fetch guest permissions eagerly in parallel with Auth0 SDK init
  const guestFetchedRef = useRef(false);
  useEffect(() => {
    if (guestFetchedRef.current) return;
    guestFetchedRef.current = true;

    setGuestLoading(true); // eslint-disable-line react-hooks/set-state-in-effect -- one-shot fetch on mount; loading flag set before the request
    fetch('/api/auth/guest-permissions')
      .then((res) => res.json())
      .then((data) => setGuestPermissions(data.permissions || []))
      .catch(() => setGuestPermissions([]))
      .finally(() => setGuestLoading(false));
  }, []);

  // Redirect unauthenticated users to login page
  const loginPromptedRef = useRef(false);
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated && guestLoading) return;

    const pagePermission = getPagePermission(internalPathname);

    if (!isAuthenticated) {
      if (
        pagePermission &&
        guestPermissions &&
        !guestPermissions.includes(pagePermission)
      ) {
        if (!loginPromptedRef.current) {
          loginPromptedRef.current = true;
          loginRedirect();
        }
      }
    } else {
      loginPromptedRef.current = false;
      if (internalPathname && !checkPagePermission(session, internalPathname)) {
        router.push('/permission-denied');
      }
    }
  }, [
    isLoading,
    isAuthenticated,
    session,
    router,
    internalPathname,
    guestPermissions,
    guestLoading,
    loginRedirect,
  ]);

  // Determine if the current page requires a permission check
  const pagePermission = internalPathname
    ? getPagePermission(internalPathname)
    : undefined;
  const needsPermissionGate = !!pagePermission;

  // Pages behind a permission check must not render their content before we
  // know the answer — but the app shell must. Replacing the whole tree with
  // a loader (as this did) unmounts the header and nav, so a slow or failing
  // session/permission fetch left the user staring at "Loading…" with no way
  // to navigate anywhere else. Keep the shell, gate only the children.
  const gatingContent =
    needsPermissionGate && (isLoading || (!isAuthenticated && guestLoading));

  // Block content render for a permission-denied redirect (the effect above
  // is already pushing to /permission-denied) — again, shell stays up.
  const denied =
    isAuthenticated &&
    internalPathname &&
    !checkPagePermission(session, internalPathname);

  return (
    <>
      <Demo7Layout>
        {gatingContent ? <ContentLoader /> : denied ? null : children}
      </Demo7Layout>
    </>
  );
}

/**
 * In-flow loading placeholder for gated page content.
 *
 * Deliberately NOT `ScreenLoader`, which is `fixed inset-0` and therefore
 * covers the header and navigation as well as the content.
 */
function ContentLoader() {
  return (
    <div
      className="flex min-h-[50vh] items-center justify-center"
      data-nosnippet
    >
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        <span>Loading…</span>
      </div>
    </div>
  );
}
